import { currencyFractionDigits } from '../../../shared/utils/commercial-money';
import { AccountingDirectoryControlsComponent } from '../../shared/accounting-directory-controls.component';
import { isEditableFinancialDraft } from '../../shared/accounting-draft-eligibility';
import { Component, OnInit, inject } from '@angular/core';
import { MenuItem, MessageService } from "primeng/api";
import { FinancePdfViewButtonComponent } from '../../finance-pdf-view-button.component';
import { AccountsService } from "../../accounts.service";
import { SessionStorageService } from "../../../services/session-storage.service";
import * as XLSX from "xlsx";
import { Invoice } from "../../invoice/invoice.model";
import { InvoiceLine } from "../../invoice/invoice-line.model";
import { TableLazyLoadEvent, TableModule } from "primeng/table";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { ToastModule } from "primeng/toast";
import { ProgressBarModule } from "primeng/progressbar";
import { PanelModule } from "primeng/panel";
import { ImageModule } from "primeng/image";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { DrawerModule } from "primeng/drawer";
import { SelectModule } from "primeng/select";
import { InputNumberModule } from "primeng/inputnumber";
import { PermissionService } from '../../../security/permission.service';
import { app_permissions } from '../../../security/app-permissions';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../../shared/page-layout/page-search-panel.component';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { Router } from '@angular/router';
import { CompanyScopeSelection, CompanyScopeSelectorComponent } from '../../company-scope-selector/company-scope-selector.component';
import { CRM_RECEIVABLE_ROLES, CrmPartyContextService } from '../../../shared/crm-client-contact-picker/crm-party-context.service';
import { LocaleService } from '../../../i18n/locale.service';
import { finalize } from 'rxjs';
import { PendingActionTracker } from '../../../shared/state/pending-action-tracker';



@Component({
    selector: 'app-generated-invoices',
    templateUrl: './generated-invoices.component.html',
    styleUrls: ['../../finance-list.shared.css', './generated-invoices.component.css'],
    providers: [
        MessageService,
        AccountsService,
        SessionStorageService
    ],
    standalone: true,
    imports: [FinancePdfViewButtonComponent,
        CommonModule,
        FormsModule,
        BreadcrumbModule,
        TableModule,
        MultiSelectModule,
        DatePickerModule,
        InputTextModule,
        ButtonModule,
        CardModule,
        TooltipModule,
        TagModule,
        ToastModule,
        ProgressBarModule,
        PanelModule,
        ImageModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        DrawerModule,
        SelectModule,
        InputNumberModule,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageSearchPanelComponent,
        PageDataPanelComponent,
        AccountingDirectoryControlsComponent,
        CompanyScopeSelectorComponent
    ]
})
export class GeneratedInvoicesComponent implements OnInit {
    openInvoiceDispatches(): void {
        this.router.navigate(['/accounts/enterprise/receivables/invoice-dispatches']);
    }
    readonly pendingStatuses = ['DRAFT', 'SUBMITTED', 'APPROVED', 'ON_HOLD'];
    searchText = '';
    directoryMode: 'LIST' | 'CRM' | 'PARTY_TYPE' = 'LIST';
    directoryFilters: Record<string, string | number | boolean | number[] | string[] | null | undefined> = {};
    private listRequestId = 0;

    readonly documentActions = new PendingActionTracker<number | undefined>();
    private readonly localeService = inject(LocaleService);

    isLoading = false;

    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = {};

    // Data properties for the unified table
    allInvoices: Invoice[] = [];
    selectedInvoices: Invoice[] = [];
    totalRecords: number = 0;
    first: number = 0;
    rows: number = 20;

    // Filter properties
    clients: any[] = [];
    selectedClients: any[] = [];
    dateRange: (Date | null)[] | null = null;
    selectedStatuses: string[] = [];
    selectedDocumentStatuses: string[] = this.pendingStatuses.slice();
    selectedDueStatus: string | null = null;
    selectedSentStatuses: string[] = [];
    selectedZatcaStatuses: string[] = [];
    selectedCollectionStatuses: string[] = [];
    currencyCodeFilter: string | null = null;
    amountMinFilter: number | null = null;
    amountMaxFilter: number | null = null;
    invoiceNumberFilter: string | null = null;

    // Summary Card properties
    totalUnpaidAmount: number = 0;
    totalPaidAmount: number = 0;
    reportCurrencyCode: string = 'USD';

    canGenerateReceipt = false;
    canEditInvoice = false;
    previewInvoice: Invoice | null = null;
    isPreviewVisible = false;
    currentScope: CompanyScopeSelection | null = null;

    get isConsolidatedReadOnly(): boolean {
        return !!this.currentScope?.readOnly;
    }

    constructor(
        private accountsService: AccountsService,
        private messageService: MessageService,
        private sessionStorageService: SessionStorageService,
        private permissionService: PermissionService,
        private router: Router,
        public crmPartyContext: CrmPartyContextService
    ) { }


    ngOnInit(): void {
        this.canGenerateReceipt = this.permissionService.hasPermission(app_permissions.accounts.receipt.create);
        this.canEditInvoice = this.permissionService.hasPermission(app_permissions.accounts.invoice.edit);
        this.breadcrumbItems = [
            { label: this.t('pages.generatedInvoices.breadcrumb.accounts') },
            { label: this.t('pages.generatedInvoices.breadcrumb.enterprise') },
            { label: this.t('pages.generatedInvoices.breadcrumb.receivables') },
            { label: this.t('pages.generatedInvoices.breadcrumb.generatedInvoices') }
        ];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };
        this.loadInitialData();
    }

    onScopeChange(scope: CompanyScopeSelection) {
        this.currentScope = scope;
        this.selectedInvoices = [];
        this.applyFilters();
    }

    loadInitialData() {
        this.isLoading = true;
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) {
            this.messageService.add({ severity: 'error', summary: this.t('common.error'), detail: this.t('pages.generatedInvoices.messages.companyNotFound') });
            this.isLoading = false;
            return;
        }

        this.crmPartyContext.loadClients(companyId, CRM_RECEIVABLE_ROLES).subscribe(clients => {
            this.clients = clients.map(client => ({ ...client, name: this.crmPartyContext.clientName(client) }));
        });

        // Initial data load for the table and summary is triggered by applyFilters
        this.applyFilters();
    }


    loadData(event: TableLazyLoadEvent) {
        this.isLoading = true;
        const requestId = ++this.listRequestId;
        this.first = event.first ?? 0;
        this.rows = event.rows ?? 20;

        const sessionCompanyId = this.sessionStorageService.getCompany()?.id;
        const companyGroupId = this.currentScope?.companyGroupId;
        const companyId = companyGroupId ? undefined : (this.currentScope?.companyId ?? sessionCompanyId);
        if (!companyId && !companyGroupId) return;

        const clientIds = this.selectedClients.map(c => Number(c.id?.entityId ?? c.id)).filter(Boolean);
        const startDate = this.dateRange?.[0] ? this.dateRange[0].toISOString() : undefined;
        const endDate = this.dateRange?.[1] ? this.dateRange[1].toISOString() : undefined;

        // Phase 1 debt: invoice payment status is still derived from the current page payload.
        // Later phases must move status/open-amount filtering fully behind backend-authoritative fields.
        this.directoryFilters = { companyId, companyGroupId, clientIds, startDate, endDate, invoiceNumber: this.invoiceNumberFilter, paymentStatuses: this.selectedStatuses, dueStatus: this.selectedDueStatus, sentStatuses: this.selectedSentStatuses, collectionStatuses: this.selectedCollectionStatuses, zatcaStatuses: this.selectedZatcaStatuses, currencyCode: this.currencyCodeFilter, amountMin: this.amountMinFilter, amountMax: this.amountMaxFilter, searchText: this.searchText };

        this.accountsService.getGeneratedInvoices(
            this.first / this.rows,
            this.rows,
            companyId,
            clientIds,
            [],
            startDate,
            endDate,
            this.invoiceNumberFilter ?? undefined,
            {
                paymentStatuses: this.selectedStatuses,
                documentStatuses: this.selectedDocumentStatuses,
                dueStatus: this.selectedDueStatus ?? undefined,
                sentStatuses: this.selectedSentStatuses,
                collectionStatuses: this.selectedCollectionStatuses,
                zatcaStatuses: this.selectedZatcaStatuses,
                currencyCode: this.currencyCodeFilter ?? undefined,
                amountMin: this.amountMinFilter ?? undefined,
                amountMax: this.amountMaxFilter ?? undefined
            },
            companyGroupId,
            this.searchText
        ).subscribe({
            next: (dataWrapper) => {
                if (requestId !== this.listRequestId) return;
                this.allInvoices = dataWrapper.items.map(i => ({
                    ...i,
                    status: this.getInvoiceStatus(i)
                }));
                this.totalRecords = dataWrapper.count;
                this.isLoading = false;
            },
            error: (err) => {
                if (requestId !== this.listRequestId) return;
                this.messageService.add({ severity: 'error', summary: this.t('common.error'), detail: this.t('pages.generatedInvoices.messages.couldNotLoad') });
                this.isLoading = false;
            }
        });

        // NOTE: This assumes 'getGeneratedInvoicesSummary' is updated to handle filters for accurate summary cards.
        this.accountsService.getGeneratedInvoicesSummary(
            companyGroupId ? undefined : companyId,
            clientIds,
            [],
            startDate,
            endDate,
            this.invoiceNumberFilter ?? undefined,
            {
                paymentStatuses: this.selectedStatuses,
                documentStatuses: this.selectedDocumentStatuses,
                dueStatus: this.selectedDueStatus ?? undefined,
                sentStatuses: this.selectedSentStatuses,
                collectionStatuses: this.selectedCollectionStatuses,
                zatcaStatuses: this.selectedZatcaStatuses,
                currencyCode: this.currencyCodeFilter ?? undefined,
                amountMin: this.amountMinFilter ?? undefined,
                amountMax: this.amountMaxFilter ?? undefined
            },
            companyGroupId
        ).subscribe(summary => {
                if (requestId !== this.listRequestId) return;
            this.totalPaidAmount = summary.totalPaidAmount
            this.totalUnpaidAmount = summary.totalUnpaidAmount
            this.reportCurrencyCode = summary.currencyCode;
        });
    }

    getInvoiceStatus(invoice: Invoice): string {
        const paidAmount = this.getPaidAmount(invoice);
        const totalAmount = invoice.totalAmount || 0;

        if (paidAmount >= totalAmount) {
            return 'PAID';
        } else if (paidAmount > 0 && paidAmount < totalAmount) {
            return 'PARTIALLY_PAID';
        } else {
            return 'UNPAID';
        }
    }

    onDirectoryModeChange(mode: 'LIST' | 'CRM' | 'PARTY_TYPE'): void {
        this.directoryMode = mode;
        this.selectedInvoices = [];
    }

    onDirectorySearch(value: string): void {
        this.searchText = value;
        this.selectedInvoices = [];
        this.applyFilters();
    }

    showPendingStatuses(): void {
        this.selectedDocumentStatuses = this.pendingStatuses.slice();
        this.applyFilters();
    }

    showAllStatuses(): void {
        this.selectedDocumentStatuses = [];
        this.applyFilters();
    }

    applyFilters() {
        // Reset to the first page when applying filters
        this.loadData({ first: 0, rows: this.rows });
    }

    clearFilters() {
        this.selectedClients = [];
        this.dateRange = null;
        this.selectedStatuses = [];
        this.selectedDocumentStatuses = this.pendingStatuses.slice();
        this.selectedDueStatus = null;
        this.selectedSentStatuses = [];
        this.selectedZatcaStatuses = [];
        this.selectedCollectionStatuses = [];
        this.currencyCodeFilter = null;
        this.amountMinFilter = null;
        this.amountMaxFilter = null;
        this.invoiceNumberFilter = null;
        this.searchText = '';
        this.applyFilters();
    }

    get metrics(): PageMetric[] {
        this.localeService.locale();
        return [
            { label: this.t('pages.generatedInvoices.metrics.openAr'), value: this.formatMoney(this.totalUnpaidAmount || this.currentPageOpenAmount()), hint: this.t('pages.generatedInvoices.metrics.openArHint'), tone: 'danger' },
            { label: this.t('pages.generatedInvoices.metrics.overdueAr'), value: this.formatMoney(this.currentPageOverdueAmount()), hint: this.t('pages.generatedInvoices.metrics.overdueArHint'), tone: 'warn' },
            { label: this.t('pages.generatedInvoices.metrics.dueThisWeek'), value: this.formatMoney(this.currentPageDueThisWeekAmount()), hint: this.t('pages.generatedInvoices.metrics.dueThisWeekHint'), tone: 'info' },
            { label: this.t('pages.generatedInvoices.metrics.zatcaFailed'), value: this.currentPageCount(invoice => invoice.zatcaStatus === 'FAILED'), hint: this.t('pages.generatedInvoices.metrics.zatcaFailedHint'), tone: 'danger' },
            { label: this.t('pages.generatedInvoices.metrics.sentUnpaid'), value: this.currentPageCount(invoice => ['SENT', 'RESENT'].includes(invoice.sentStatus || '') && this.getOpenAmount(invoice) > 0), hint: this.t('pages.generatedInvoices.metrics.sentUnpaidHint'), tone: 'warn' },
            { label: this.t('pages.generatedInvoices.metrics.disputed'), value: this.currentPageCount(invoice => invoice.collectionStatus === 'DISPUTED'), hint: this.t('pages.generatedInvoices.metrics.disputedHint'), tone: 'danger' }
        ];
    }

    get statusOptions() {
        this.localeService.locale();
        return [
            { label: this.t('pages.generatedInvoices.paymentStatus.paid'), value: 'PAID' },
            { label: this.t('pages.generatedInvoices.paymentStatus.partiallyPaid'), value: 'PARTIALLY_PAID' },
            { label: this.t('pages.generatedInvoices.paymentStatus.unpaid'), value: 'UNPAID' }
        ];
    }

    get documentStatusOptions() {
        this.localeService.locale();
        return [
            { label: this.t('pages.generatedInvoices.documentStatus.draft'), value: 'DRAFT' },
            { label: this.t('pages.generatedInvoices.documentStatus.submitted'), value: 'SUBMITTED' },
            { label: this.t('pages.generatedInvoices.documentStatus.approved'), value: 'APPROVED' },
            { label: this.t('pages.generatedInvoices.documentStatus.posted'), value: 'POSTED' },
            { label: this.t('pages.generatedInvoices.documentStatus.onHold'), value: 'ON_HOLD' },
            { label: this.t('pages.generatedInvoices.documentStatus.cancelled'), value: 'CANCELLED' },
            { label: this.t('pages.generatedInvoices.documentStatus.voided'), value: 'VOIDED' },
            { label: this.t('pages.generatedInvoices.documentStatus.reversed'), value: 'REVERSED' },
            { label: this.t('pages.generatedInvoices.documentStatus.closed'), value: 'CLOSED' }
        ];
    }

    get dueStatusOptions() {
        this.localeService.locale();
        return [
            { label: this.t('pages.generatedInvoices.dueStatus.open'), value: 'OPEN' },
            { label: this.t('pages.generatedInvoices.dueStatus.overdue'), value: 'OVERDUE' },
            { label: this.t('pages.generatedInvoices.dueStatus.current'), value: 'CURRENT' },
            { label: this.t('pages.generatedInvoices.dueStatus.noDueDate'), value: 'NO_DUE_DATE' }
        ];
    }

    get sentStatusOptions() {
        this.localeService.locale();
        return [
            { label: this.t('pages.generatedInvoices.sentStatus.notSent'), value: 'NOT_SENT' },
            { label: this.t('pages.generatedInvoices.sentStatus.sent'), value: 'SENT' },
            { label: this.t('pages.generatedInvoices.sentStatus.resent'), value: 'RESENT' },
            { label: this.t('pages.generatedInvoices.sentStatus.failed'), value: 'FAILED' }
        ];
    }

    get zatcaStatusOptions() {
        this.localeService.locale();
        return [
            { label: this.t('pages.generatedInvoices.zatcaStatus.pending'), value: 'PENDING' },
            { label: this.t('pages.generatedInvoices.zatcaStatus.reported'), value: 'REPORTED' },
            { label: this.t('pages.generatedInvoices.zatcaStatus.cleared'), value: 'CLEARED' },
            { label: this.t('pages.generatedInvoices.zatcaStatus.failed'), value: 'FAILED' },
            { label: this.t('pages.generatedInvoices.zatcaStatus.notRequired'), value: 'NOT_REQUIRED' }
        ];
    }

    get collectionStatusOptions() {
        this.localeService.locale();
        return [
            { label: this.t('pages.generatedInvoices.collectionStatus.none'), value: 'NONE' },
            { label: this.t('pages.generatedInvoices.collectionStatus.current'), value: 'CURRENT' },
            { label: this.t('pages.generatedInvoices.collectionStatus.overdue'), value: 'OVERDUE' },
            { label: this.t('pages.generatedInvoices.collectionStatus.disputed'), value: 'DISPUTED' },
            { label: this.t('pages.generatedInvoices.collectionStatus.promiseToPay'), value: 'PROMISE_TO_PAY' },
            { label: this.t('pages.generatedInvoices.collectionStatus.collections'), value: 'COLLECTIONS' },
            { label: this.t('pages.generatedInvoices.collectionStatus.onHold'), value: 'ON_HOLD' }
        ];
    }

    get activeFilterCount(): number {
        return [
            this.selectedClients.length,
            this.dateRange?.some(Boolean) ? 1 : 0,
            this.selectedStatuses.length,
            this.selectedDocumentStatuses.length,
            this.selectedDueStatus ? 1 : 0,
            this.selectedSentStatuses.length,
            this.selectedZatcaStatuses.length,
            this.selectedCollectionStatuses.length,
            this.currencyCodeFilter ? 1 : 0,
            this.amountMinFilter !== null ? 1 : 0,
            this.amountMaxFilter !== null ? 1 : 0,
            this.invoiceNumberFilter ? 1 : 0
        ].reduce((total, value) => total + Number(value), 0);
    }

    get resultText(): string {
        this.localeService.locale();
        return this.t('pages.generatedInvoices.results.invoices', { count: this.totalRecords });
    }

    get currentPageReportTemplate(): string {
        this.localeService.locale();
        return this.t('pages.generatedInvoices.table.pageReport');
    }

    getSelectedTotal(): number {
        return this.selectedInvoices.reduce((total, invoice) => total + (invoice.totalAmount || 0), 0);
    }

    getPaidAmount(invoice: Invoice): number {
        return invoice.allocatedAmount ?? invoice.receipts?.reduce((acc, receipt) => acc + (receipt.allocatedAmount ?? receipt.amount ?? 0), 0) ?? 0;
    }

    getOpenAmount(invoice: Invoice): number {
        return invoice.openAmount ?? Math.max(0, (invoice.totalAmount || 0) - this.getPaidAmount(invoice));
    }

    currentPageOpenAmount(): number {
        return this.allInvoices.reduce((total, invoice) => total + this.getOpenAmount(invoice), 0);
    }

    currentPageOverdueAmount(): number {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return this.allInvoices
            .filter(invoice => invoice.dueDate && new Date(invoice.dueDate).getTime() < today.getTime() && this.getOpenAmount(invoice) > 0)
            .reduce((total, invoice) => total + this.getOpenAmount(invoice), 0);
    }

    currentPageDueThisWeekAmount(): number {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const nextWeek = new Date(today);
        nextWeek.setDate(today.getDate() + 7);
        return this.allInvoices
            .filter(invoice => {
                if (!invoice.dueDate || this.getOpenAmount(invoice) <= 0) return false;
                const dueDate = new Date(invoice.dueDate);
                return dueDate >= today && dueDate <= nextWeek;
            })
            .reduce((total, invoice) => total + this.getOpenAmount(invoice), 0);
    }

    currentPageCount(predicate: (invoice: Invoice) => boolean): number {
        return this.allInvoices.filter(predicate).length;
    }

    formatMoney(amount: number | undefined, currencyCode: string = this.reportCurrencyCode): string {
        return `${currencyCode || ''} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: currencyFractionDigits(currencyCode || 'SAR'), maximumFractionDigits: currencyFractionDigits(currencyCode || 'SAR') })}`;
    }

    statusSeverity(status: string | undefined): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (status === 'PAID') return 'success';
        if (status === 'PARTIALLY_PAID') return 'warn';
        if (status === 'UNPAID') return 'danger';
        if (status === 'DRAFT' || status === 'SUBMITTED') return 'info';
        if (status === 'APPROVED') return 'success';
        if (status === 'POSTED' || status === 'CLOSED') return 'success';
        if (status === 'ON_HOLD') return 'warn';
        if (status === 'CANCELLED' || status === 'VOIDED' || status === 'REVERSED') return 'danger';
        return 'secondary';
    }

    paymentStatusLabel(status: string | undefined): string {
        this.localeService.locale();
        switch (status) {
            case 'PAID': return this.t('pages.generatedInvoices.paymentStatus.paid');
            case 'PARTIALLY_PAID': return this.t('pages.generatedInvoices.paymentStatus.partiallyPaid');
            case 'UNPAID': return this.t('pages.generatedInvoices.paymentStatus.unpaid');
            default: return this.t('pages.generatedInvoices.paymentStatus.unknown');
        }
    }

    documentStatusLabel(status: string | undefined): string {
        this.localeService.locale();
        switch (status?.trim().toUpperCase()) {
            case 'DRAFT': return this.t('pages.generatedInvoices.documentStatus.draft');
            case 'SUBMITTED': return this.t('pages.generatedInvoices.documentStatus.submitted');
            case 'APPROVED': return this.t('pages.generatedInvoices.documentStatus.approved');
            case 'POSTED': return this.t('pages.generatedInvoices.documentStatus.posted');
            case 'ON_HOLD': return this.t('pages.generatedInvoices.documentStatus.onHold');
            case 'CANCELLED': return this.t('pages.generatedInvoices.documentStatus.cancelled');
            case 'VOIDED': return this.t('pages.generatedInvoices.documentStatus.voided');
            case 'REVERSED': return this.t('pages.generatedInvoices.documentStatus.reversed');
            case 'CLOSED': return this.t('pages.generatedInvoices.documentStatus.closed');
            default: return status?.trim() || this.t('pages.generatedInvoices.documentStatus.noLifecycle');
        }
    }

    viewInvoice(invoice: Invoice) {
        if (invoice.invoiceId) {
            this.router.navigate(['/accounts/enterprise/receivables/generated-invoices/view', invoice.invoiceId]);
        }
    }

    createManualInvoiceDraft() {
        this.router.navigate(['/accounts/enterprise/receivables/generated-invoices/manual-draft']);
    }

    canEditDraftInvoice(invoice: Invoice): boolean {
        const documentStatus = invoice.documentStatus;
        return !this.isConsolidatedReadOnly
            && this.canEditInvoice
            && !!invoice.invoiceId
            && isEditableFinancialDraft(invoice, this.sessionStorageService.getCompany()?.id);
    }

    editInvoice(invoice: Invoice) {
        if (!this.canEditDraftInvoice(invoice)) return;
        this.router.navigate(['/accounts/enterprise/receivables/generated-invoices/manual-draft', invoice.invoiceId]);
    }

    postInvoice(invoice: Invoice) {
        if (this.isConsolidatedReadOnly) {
            this.messageService.add({ severity: 'warn', summary: this.t('pages.generatedInvoices.messages.readOnlyScopeSummary'), detail: this.t('pages.generatedInvoices.messages.postInvoicesReadOnly') });
            return;
        }
        if (!invoice.invoiceId) return;
        this.isLoading = true;
        this.accountsService.postInvoice(invoice.invoiceId, { source: 'PORTAL', comment: 'Posted from invoice list' }).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: this.t('pages.generatedInvoices.messages.postedSummary'), detail: this.t('pages.generatedInvoices.messages.invoicePosted') });
                this.applyFilters();
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: this.t('pages.generatedInvoices.messages.postFailedSummary'), detail: err.error?.message || this.t('pages.generatedInvoices.messages.couldNotPostInvoice') });
                this.isLoading = false;
            }
        });
    }

    openPreview(invoice: Invoice) {
        this.previewInvoice = invoice;
        this.isPreviewVisible = true;
    }


    downloadInvoice(invoice: Invoice) {
        if (!invoice.invoiceId || !this.documentActions.begin(invoice.invoiceId)) return;
        this.accountsService.downloadInvoiceDocument(invoice.invoiceId).pipe(
            finalize(() => this.documentActions.end(invoice.invoiceId))
        ).subscribe({
            next: pdf => this.accountsService.downloadPdfDocument(pdf, 'invoice', invoice.invoiceNumber, invoice.invoiceId, invoice.postingStatus),
            error: err => this.messageService.add({ severity: 'error', summary: 'Document unavailable', detail: err?.status === 404 ? 'Original unavailable: no stored invoice PDF exists.' : err?.error?.message || 'The invoice PDF is unavailable.' })
        });
    }

    exportToExcel() {
        if (this.allInvoices.length === 0) {
            this.messageService.add({ severity: 'warn', summary: this.t('pages.generatedInvoices.messages.noDataSummary'), detail: this.t('pages.generatedInvoices.messages.noDataDetail') });
            return;
        }
        const worksheet = XLSX.utils.json_to_sheet(this.allInvoices.map(i => ({
            Client: this.crmPartyContext.displayName(i),
            Contact: this.crmPartyContext.contactDisplayName(i),
            'Invoice #': i.invoiceNumber,
            'Invoice Date': i.invoiceCreationDate,
            'Total Amount': i.totalAmount,
            Currency: i.country.currencyCode,
            Status: this.getInvoiceStatus(i),
            'Applied Receipts': i.receipts.map(r => r.receiptNumber).join(', ')
        })));
        const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
        const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        this.saveAsExcelFile(excelBuffer, "GeneratedInvoicesExport");
    }

    private saveAsExcelFile(buffer: any, fileName: string): void {
        // Create a blob from the workbook
        const blob = new Blob([XLSX.write(buffer, { bookType: 'xlsx', type: 'array' })],
            { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        // Create a URL for the blob
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.target = '_blank';
        a.click();

        // Clean up by revoking the URL
        window.URL.revokeObjectURL(url);
    }

    editInvoiceLine(invoiceLine: InvoiceLine) {
        // Implement logic to edit an invoice line, e.g., open a dialog
        console.log('Editing invoice line:', invoiceLine);
    }

    generateReceipt() {
        if (this.isConsolidatedReadOnly) {
            this.messageService.add({ severity: 'warn', summary: this.t('pages.generatedInvoices.messages.readOnlyScopeSummary'), detail: this.t('pages.generatedInvoices.messages.generateReceiptsReadOnly') });
            return;
        }
        if (!this.selectedInvoices || this.selectedInvoices.length === 0) {
            this.messageService.add({ severity: 'error', summary: this.t('common.error'), detail: this.t('pages.generatedInvoices.messages.selectAtLeastOneInvoice') });
            return;
        }
        // Check if any selected invoice is already fully paid
        if (this.selectedInvoices.some(inv => this.getInvoiceStatus(inv) === 'PAID')) {
            this.messageService.add({ severity: 'error', summary: this.t('common.error'), detail: this.t('pages.generatedInvoices.messages.alreadyFullyPaid') });
            return;
        }
        this.isLoading = true;
        this.accountsService.generateReceiptFromInvoices(this.selectedInvoices).subscribe({
            next: (receipt) => {
                this.messageService.add({ severity: 'success', summary: this.t('common.success'), detail: this.t('pages.generatedInvoices.messages.receiptGenerated', { receiptNumber: receipt.receiptNumber }) });
                this.selectedInvoices = []; // Clear selection
                this.applyFilters(); // Refresh data
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: this.t('pages.generatedInvoices.messages.receiptErrorSummary'), detail: this.t('pages.generatedInvoices.messages.receiptErrorPrefix') + (err.error?.message || this.t('pages.generatedInvoices.messages.unknownError')) });
                this.isLoading = false;
            }
        });
    }

    t(key: string, fallbackOrParams?: string | Record<string, unknown>, params?: Record<string, unknown>): string {
        if (typeof fallbackOrParams === 'string') {
            return this.localeService.translateIfPresent(key, fallbackOrParams, params);
        }
        return this.localeService.translate(key, fallbackOrParams);
    }

}
